import { Box, Grid, Typography } from '@mui/material';
import { PersonFilledIcon } from 'components/common/Icon';
import LinearProgress from '@mui/material/LinearProgress';
import {
  useCompanySetupState,
  useCurrentCompany,
  useCurrentUserRole,
  useNavigate,
} from 'hooks/api';
import { useMemo } from 'react';
import { CompanySetupState } from 'models/company';
import { Routes } from 'fnRoutes';
import { isBusinessFullSuite } from 'components/ProgressTracker/util';
import { BusinessStatusLabel } from 'models/business';

const TotalSteps = 13;
export const getProgressStepCount = (
  companySetupState?: CompanySetupState,
  isDisabledPayroll: boolean = false,
) => {
  if (!companySetupState) return 0;

  const payrollSteps = [
    'directDepositInformation',
    'reasonableCompensationStudy',
    'payrollOnboarding',
    'payrollPlanApproval',
  ];
  const steps = [
    'businessConfiguration',
    'shareableBusinessPacket',
    'personalProfile',
    'businessProfile',
    'lastYearTaxReturn',
    'yearToDateCatchUp',
    'bankConnections',
    'businessUseOfHome',
    ...payrollSteps,
    'squarespaceAddOn',
  ];

  return steps.reduce((count, step) => {
    if (companySetupState[step as keyof CompanySetupState]) {
      if (isDisabledPayroll && payrollSteps.includes(step)) {
        return count;
      }
      return count + 1;
    }
    return count;
  }, 0);
};

export const getTotalSteps = (isDisabledPayroll: boolean) =>
  isDisabledPayroll ? TotalSteps - 4 : TotalSteps;

export const SetupProgress = () => {
  const navigate = useNavigate();
  const { currentCompany } = useCurrentCompany();
  const { companySetupState } = useCompanySetupState(currentCompany?.id);
  const { isAdmin } = useCurrentUserRole();
  const total = getTotalSteps(currentCompany?.disabledPayroll ?? false);
  const progressCount = useMemo(
    () =>
      getProgressStepCount(
        companySetupState,
        currentCompany?.disabledPayroll ?? false,
      ),
    [companySetupState, currentCompany?.disabledPayroll],
  );
  const showSetupProgress = useMemo(() => {
    if (isAdmin) return false;
    if (currentCompany?.status !== BusinessStatusLabel.ACTIVE) return false;
    if (!isBusinessFullSuite(currentCompany)) return false;
    return progressCount < total;
  }, [isAdmin, currentCompany, progressCount, total]);

  return showSetupProgress ? (
    <Grid
      container
      flexDirection="row"
      alignItems="flex-start"
      justifyContent="flex-start"
      sx={{ cursor: 'pointer', p: 2 }}
      onClick={() => {
        navigate(Routes.SETUP_PAGE);
      }}
    >
      <PersonFilledIcon />
      <Grid
        item
        display="flex"
        flexDirection="column"
        justifyContent="flex-start"
        alignItems="flex-start"
        sx={{ ml: 2, flexGrow: 1, pr: 2 }}
      >
        <Typography variant="body2S" color="text.secondary" sx={{ mb: 1 }}>
          Complete set up
        </Typography>
        <Box sx={{ width: '100%' }}>
          <LinearProgress
            variant="determinate"
            value={(progressCount / total) * 100}
          />
        </Box>
        <Typography variant="body3B" sx={{ mt: 1 }}>
          {progressCount}/{total} completed
        </Typography>
      </Grid>
    </Grid>
  ) : null;
};
